/**
 * Loading pack art.
 *
 * Every family in the pack - items, menu icons, hats, the pup, the Lookout - is fetched, parsed and
 * inlined the same way, and by stage F there were four copies of these twenty lines: two in
 * `content/`, and `defaultLoadHat` written out twice in `shell/`. The scene on the Lookout needed a
 * fifth, which is one too many.
 *
 * Inline rather than an `<img>` so the art inherits its size from CSS, stays crisp at any tablet DPI, and
 * can be tinted with `currentColor` - which is what the celebration burst needs.
 */

/**
 * Where a piece of pack art lives.
 *
 * One place that knows the layout, because the path shape was written out in five files and a content
 * pack is meant to be something a parent can swap. `family` is a directory in the pack: `items`, `ui`,
 * `hats`, `pups`, `stickers`, `vehicles`, `deco`.
 */
export function packUrl(family: string, name: string, pack = 'patrol'): string {
  return `${import.meta.env.BASE_URL}content/packs/${pack}/${family}/${name}.svg`;
}

/**
 * Cached by URL. The same file is asked for by several screens at once - the Lookout icon appears in the
 * HUD, the ribbon, both celebration beats and now the menu - and it must not be fetched once per caller.
 */
const textCache = new Map<string, Promise<string>>();

/**
 * Warned about once each.
 *
 * A failure is retried on the next screen (the cache entry is dropped), so without this a missing file
 * logs once per mount - which in a test run buries whatever actually went wrong under a hundred copies of
 * the same line.
 */
const warned = new Set<string>();

function warnOnce(url: string, detail: unknown): void {
  if (warned.has(url)) return;
  warned.add(url);
  console.warn(`[pawp] could not load ${url}`, detail);
}

export function fetchSvgText(url: string): Promise<string> {
  let pending = textCache.get(url);
  if (pending === undefined) {
    pending = fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error(`${url}: HTTP ${response.status}`);
        return response.text();
      })
      .catch((error: unknown) => {
        // Dropped from the cache, so a transient failure is retried on the next screen rather than
        // remembered for the rest of the session.
        textCache.delete(url);
        throw error;
      });
    textCache.set(url, pending);
  }
  return pending;
}

/**
 * Prepared elements, ready to be copied.
 *
 * The text cache above saves the fetch; this saves the *parse*, which is the expensive half. A screen
 * swap inlines fifteen or sixteen icons - the ribbon, the HUD, the menu, the badges - and re-parsing the
 * same handful of files each time costs tens of milliseconds on the old tablet, right where a screen is
 * supposed to appear at once. Nothing here is ever handed out: callers get a deep copy, because they go
 * on to size and class and colour what they are given, and two screens showing the Lookout must not be
 * looking at one element.
 */
const elementCache = new Map<string, SVGSVGElement>();

/**
 * An inline `<svg>`, or null.
 *
 * Null is an expected outcome rather than an error to handle: every caller has a fallback - a glyph, a
 * blank card, a sad pup - because a missing picture must never cost her the control it sits on.
 */
export async function inlineSvg(url: string): Promise<SVGSVGElement | null> {
  const prepared = elementCache.get(url);
  if (prepared !== undefined) return prepared.cloneNode(true) as SVGSVGElement;

  try {
    const text = await fetchSvgText(url);
    const parsed = new DOMParser().parseFromString(text, 'image/svg+xml');
    /*
     * A duplicate attribute is enough to fail this, and it fails silently: two stickers shipped that way
     * once and their tray buttons were simply empty, with nothing logged anywhere.
     */
    if (parsed.querySelector('parsererror') !== null) {
      warnOnce(url, 'not valid XML');
      return null;
    }

    const svg = parsed.documentElement as unknown as SVGSVGElement;
    // The authored size goes, so CSS decides how big it is; the class is what every stylesheet rule hangs
    // off; aria-hidden because none of this art carries meaning a screen reader could use.
    svg.removeAttribute('width');
    svg.removeAttribute('height');
    svg.setAttribute('class', 'icon');
    svg.setAttribute('aria-hidden', 'true');

    elementCache.set(url, svg);
    return svg.cloneNode(true) as SVGSVGElement;
  } catch (error) {
    warnOnce(url, error);
    return null;
  }
}
